import {gql} from "@apollo/client";
import {EducationSessionResult, ErrorAudio, Challenge, Project, ResultDescription, Maybe, Scalars} from "../../schema";
import SessionActionsList from "../../components/results/SessionActionsList";
import Results, {QueryData as ResultsComponentQueryData} from "../../components/player/Results";
import {Scenario} from "../../types";

export type QueryData = {
  challenge: Challenge;
  scenarios: Scenario[];
};

export type ErrorsAudioQueryData = {
  audios: ErrorAudio[];
};

export const query = gql`
  query PlayerSceneQuery($challengeId: ID!, $externalUserId: ID, $path: String) {
    challenge: getChallenge(challengeId: $challengeId, externalUserId: $externalUserId, path: $path) {
      id
      accessToken
      name
      accountId
      languageCode
      background
      canResume
      hasPassed
      # canPlay
      avatar {
        id
        name
        gender
        previewFull
        modelType
        gltfAsset
      }
      gestures
      mode
      displayMode
      inputMode
      badWordsFilter
      inviteByLink
      selectableInputs
      alwaysCanSkipSteps
      showResultsForEachParameter
      useCharacterVoiceForErrors
      muteNonUserSpeech
      showPreviousPhrase
      showDetailedResults
      evaluateScenarios
      startMessage
      resultMessage
      timeLimit
      actionTimeLimit
      avatarVoiceId
      displayParametricResultMode
      isSessionResultFeedbackEnabled
    }

    scenarios: getChallengeScenarios(challengeId: $challengeId) {
      __typename
      id
      name
      isMain
      triggerPhrases
      afterAction
      afterActionTarget
      nextScenario
      actions {
        id
        ... on AvatarAction {
          text
          audio
          emotion
          gesture
          media
          mediaType
          mediaViewRequired
          setMediaAsBackground
          replaceCharacterWithMedia
        }

        ... on SystemAction {
          text
          audio
          media
          mediaType
          mediaViewRequired
          setMediaAsBackground
          replaceCharacterWithMedia
        }

        ... on UserInputAction {
          referenceText
          questionText
          freeSpeech
          hintText
        }

        ... on ChoiceUserInputAction {
          id
          isMultipleChoice
          questionText
          hintText
          branches {
            id
            state
            targetScenarioId
            userInput {
              id
              hintText
              referenceText
              questionText
            }

            avatarReaction {
              id
              text
              audio
              emotion
              gesture
              media
              mediaType
              mediaViewRequired
              setMediaAsBackground
              replaceCharacterWithMedia
            }

            systemReaction {
              id
              text
              audio
              media
              mediaType
              mediaViewRequired
              setMediaAsBackground
              replaceCharacterWithMedia
            }
          }
        }

        ...SessionActionsList
      }
    }
  }

  ${SessionActionsList.fragments.root}
`;

export type ResultsQueryVars = {
  sessionId: Scalars["ID"];
  externalUserId: Maybe<Scalars["ID"]>;
  challengeId: Maybe<Scalars["ID"]>;
};

export type ResultsQueryData = {
  results: Pick<EducationSessionResult, "actionResults" | "initAvatarAction"> & ResultsComponentQueryData["results"];
  resultDescriptions: ResultDescription[];
};

export const resultsQuery = gql`
  query PlayerResultsScreenQuery($sessionId: ID!, $externalUserId: ID, $challengeId: ID!) {
    results: getSessionResult(sessionId: $sessionId, externalUserId: $externalUserId) {
      ...Results
      actionResults {
        ...SessionActionsListActionResult
      }
      initAvatarAction {
        text
        audio
      }
    }

    resultDescriptions: getChallengeResultDescriptions(challengeId: $challengeId) {
      id
      minScore
      maxScore
      message
    }
  }

  ${SessionActionsList.fragments.actionsResults}
  ${Results.fragments.root}
`;

export type NextChallengeQueryVars = {
  challengeId: Challenge["id"];
  projectId?: Project["id"]
};

export type NextChallengeQueryData = {
  nextChallenge: ResultsComponentQueryData["nextChallenge"];
};

export const nextChallengeQuery = gql`
  query PlayerNextChallengeQuery($challengeId: ID!) {
    nextChallenge: getNextChallenge(challengeId: $challengeId) {
      ...NextChallenge
    }
  }

  ${Results.fragments.nextChallenge}
`;

export const errorsAudioQuery = gql`
  query ErrorsAudioQuery($voice: String!) {
    audios: getErrorsAudio(voice: $voice) {
      errorReason
      filepath
      gesture
      emotion
      variantsCount
    }
  }
`;
